import React, { useState } from 'react';
import { Modal, Upload, Button, message, Alert } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import type { RcFile } from 'antd/es/upload';
import api from '../utils/axios';

interface ClaimImportProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const ClaimImport: React.FC<ClaimImportProps> = ({ open, onClose, onSuccess }) => {
  const [file, setFile] = useState<RcFile | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleUpload = async () => {
    if (!file) {
      message.error('Please select a CSV file to upload.');
      return;
    }
    setUploading(true);
    setError(null);
    setSuccess(null);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await api.post('/claims/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (response.data.success) {
        setSuccess(response.data.message || 'Claims imported successfully!');
        message.success('Claims imported successfully!');
        setFile(null);
        onSuccess();
      } else {
        setError(response.data.message || 'Import failed.');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Import failed.');
    } finally {
      setUploading(false);
    }
  };

  const beforeUpload = (file: RcFile) => {
    setFile(file);
    setError(null);
    setSuccess(null);
    return false; // Prevent auto upload
  };

  const handleRemove = () => {
    setFile(null);
    setError(null);
    setSuccess(null);
  };

  return (
    <Modal
      title="Import Claims from CSV"
      open={open}
      onCancel={onClose}
      onOk={handleUpload}
      okText="Import"
      confirmLoading={uploading}
      okButtonProps={{ disabled: !file || uploading }}
      destroyOnHidden
    >
      <Upload
        beforeUpload={beforeUpload}
        onRemove={handleRemove}
        fileList={file ? [file] : []}
        accept=".csv"
        maxCount={1}
      >
        <Button icon={<UploadOutlined />}>Select CSV File</Button>
      </Upload>
      {error && <Alert type="error" message={error} showIcon style={{ marginTop: 16 }} />}
      {success && <Alert type="success" message={success} showIcon style={{ marginTop: 16 }} />}
      <div style={{ marginTop: 16 }}>
        <b>CSV format:</b>
        <pre style={{ background: '#f5f5f5', padding: 8, borderRadius: 4 }}>
file_number,claimNumber,status,description,amount,type,dateOfLoss,dateReported,insuredName,adjusterName,policyNumber,location,tenantId,clientId,insurerId,contactId,representativeId
CLM-001,10001,OPEN,Water damage,5000,PROPERTY,2024-01-01,2024-01-02,John Doe,Jane Smith,PN-123,123 Main St,99b61f23-3697-4db8-acef-df7dfa43ea0a,cli-001,ins-001,con-001,rep-001
CLM-002,10002,CLOSED,Fire loss,12000,PROPERTY,2024-02-01,2024-02-02,Acme Corp,Alex Johnson,PN-456,456 Market St,99b61f23-3697-4db8-acef-df7dfa43ea0a,cli-002,ins-002,con-002,rep-002
        </pre>
      </div>
    </Modal>
  );
};

export default ClaimImport; 